import { NgClass } from '@angular/common'
import { Component, DestroyRef, inject, input, OnDestroy, OnInit, signal, viewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Router } from '@angular/router'
import { InvoicePlansComponent } from '@app/modules/auth/components/invoice-plans/invoice-plans.component'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { Workspace } from '@app/modules/workspaces/interfaces/workspace.interface'
import { V2TranslationService } from '@app/v2/services/translation.service'
import { getOrganizationSettingsUrl } from '@app/v2/settings/helpers/organization-settings-url.helper'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { environment } from 'src/environments/environment'
import { ModalComponent } from '../../modal/modal.component'
import { GoToBillingCardComponent } from '../go-to-billing-card/go-to-billing-card.component'
import { SystemOptions } from '../navbar.component'
import { DrawerOrgCollectionComponent } from './drawer-org-collection/drawer-org-collection.component'
import { MobileDrawerService } from './mobile-drawer.service'

@Component({
  selector: 'app-mobile-drawer',
  templateUrl: './mobile-drawer.component.html',
  styleUrls: ['./mobile-drawer.component.scss'],
  standalone: true,
  imports: [TranslateModule, NgClass, ModalComponent, InvoicePlansComponent, GoToBillingCardComponent, DrawerOrgCollectionComponent]
})
export class MobileDrawerComponent implements OnInit, OnDestroy {
  public authService = inject(AuthService)
  public translateService = inject(TranslateService)
  public mobileDrawerService = inject(MobileDrawerService)
  private _router = inject(Router)
  private destroyRef = inject(DestroyRef)
  private v2TranslationService = inject(V2TranslationService)
  private _ModalService = inject(ModalService)
  public isOpen = signal(false)
  public selectedWorkspace = signal<SystemOptions>(SystemOptions.PLANNING)
  public showUpgradeButton = input<boolean>(false)
  public showWallet = input<boolean>(false)
  public balance = input<number | null>(null)
  public isBalanceLoaded = input<boolean>(false)
  public isModalOpen = signal<boolean>(false)
  public workspace = input<Workspace>(null)
  public canSwitchOrganization = signal<boolean>(false)
  public showOrganizationSwitcher = signal<boolean>(false)

  // Signal-based ViewChild
  drawerOrgCollection = viewChild(DrawerOrgCollectionComponent)
  ngOnInit(): void {
    this.mobileDrawerService.isOpen$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isOpen) => {
      this.isOpen.set(isOpen)

      // When drawer opens, always start with main drawer (close org switcher)
      this.showOrganizationSwitcher.set(!isOpen)
    })

    // Initialize global listeners for keyboard shortcuts
    this.mobileDrawerService.initializeGlobalListeners()
    this.canSwitchOrganization.set(this.authService.workspaces.length > 1)
  }

  ngOnDestroy(): void {
    this.mobileDrawerService.destroyGlobalListeners()
  }

  closeDrawer(): void {
    // Close organization switcher when closing drawer
    this.showOrganizationSwitcher.set(false)
    this.mobileDrawerService.close()
  }

  navigateTo(link: string): void {
    this._router.navigate([link])
    this.closeDrawer()
  }

  logout(): void {
    // Use the correct signOut method from AuthService
    this.closeDrawer()
    this.authService
      .signOut()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {})
  }

  get isRtl(): boolean {
    return this.v2TranslationService.currentLang()?.direction === 'rtl'
  }

  SystemOptions = SystemOptions
  goToCrew(): void {
    // Always use environment-configured Crew URL (staging vs production).
    window.location.href = `${environment.crewSystem}`.replace(/\/+$/, '') + '/'
  }

  // trim the user's full name
  public trimUserName(name: string): string {
    return this.authService.trimUserName(name)
  }

  get userAvatar() {
    return this.authService.avatar
  }

  get userInitials() {
    return this.authService?.userInitials || ''
  }

  get currentWorkspaceId() {
    return this.authService.currentWorkspace?.id
  }

  openUpgradeToProModal() {
    this.mobileDrawerService.close()
    this._ModalService.toggleModal('invoice-plans-modal')
    this.isModalOpen.set(true)
  }

  closeUpgradeToProModal() {
    this._ModalService.toggleModal('invoice-plans-modal')
    this.isModalOpen.set(false)
  }

  /**
   * Navigate to organization settings for the currently displayed workspace.
   */
  goToOrganizationSettings() {
    this._router.navigateByUrl(getOrganizationSettingsUrl())
    this.closeDrawer()
  }

  openAddOrganizationModal() {
    this.closeDrawer()
    this._ModalService.toggleModal('add-new-organization-modal')
  }

  /**
   * Opens the organization switcher view
   */
  public openOrganizationSwitcher(): void {
    this.showOrganizationSwitcher.set(true)
  }

  /**
   * Closes the organization switcher view
   */
  public closeOrganizationSwitcher(): void {
    this.showOrganizationSwitcher.set(false)
  }

  /**
   * Handles organization selection from the drawer (card click)
   * @param organization - Selected organization
   */
  public onOrganizationSelected(organization: Workspace): void {
    // Organization switching is handled by the drawer org collection component
    // After switching, close the entire drawer
    this.showOrganizationSwitcher.set(false)
    this.closeDrawer()
  }

  /**
   * Handles card click from the drawer org collection
   * @param organization - Selected organization
   */
  public onCardClicked(organization: Workspace): void {
    // This is the same as organizationSelected - both close the drawer
    this.onOrganizationSelected(organization)
  }

  /**
   * Handles scroll events for organization collection infinite scrolling
   * @param event - Scroll event
   */
  public onOrganizationScroll(event: Event): void {
    // Pass the scroll event to the drawer org collection component
    const drawerOrg = this.drawerOrgCollection()
    if (drawerOrg) {
      drawerOrg.onScroll(event)
    }
  }
}
